import { useEffect, useRef } from 'react';

import {
  usePlayerActorScene,
  usePlayerResolvedAnimation,
} from '@cartwhl/3d/player';

import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import { isBuiltinFreeCharacterId } from '../../../shared/freeCharacterMesh';
import {
  createEmptyKeyPoseSkeletonSnapshot,
  createSourcePoseFromApiKeyPose,
  deserializeSceneKeyPoses,
  serializeSceneKeyPoseSourceContent,
  serializeSceneKeyPoses,
} from '../keyPoseApiPayload';
import { getSourceBvhForTimeline } from '../keyPoseFrameMapping';
import { selectKeyPoseApiPayloadForReference } from '../keyPoseSelectors';
import {
  createKeyPoseSnapshotFromSourcePose,
  hasKeyPoseSkeletonSnapshotTransforms,
} from '../keyPoseSnapshots';
import {
  type ReplaceRecoveredSceneKeyPoseInput,
  useStore as useKeyPoseStore,
} from '../keyPoseStore';

export function useKeyPoseSceneRecovery() {
  const motionEditorStore = useMotionEditorStore();
  const keyPoseStore = useKeyPoseStore();
  const { scene, playback } = motionEditorStore.state;
  const activeReferenceName =
    playback.selectedSceneMotion?.referenceName ??
    playback.selectedReferenceName;
  const activeTimelineIndex =
    playback.selectedSceneMotion?.timelineIndex ?? null;
  const actorScene = usePlayerActorScene(activeReferenceName ?? undefined);
  const resolvedAnimation = usePlayerResolvedAnimation(
    activeReferenceName ?? undefined,
  );
  const sourceBvh = getSourceBvhForTimeline(
    resolvedAnimation.bvh,
    resolvedAnimation.bvhs,
    activeTimelineIndex,
  );
  const sceneID = scene.data?.sceneID ?? null;

  const lastRecoveredPayloadByKeyRef = useRef<Map<string, string>>(new Map());
  const lastSceneIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (sceneID !== lastSceneIdRef.current) {
      lastRecoveredPayloadByKeyRef.current.clear();
      lastSceneIdRef.current = sceneID;
    }

    if (!sceneID || !scene.data) {
      return;
    }

    for (const actor of playback.sceneActors) {
      const referenceName = actor.referenceName;
      const timelineIndex =
        referenceName === activeReferenceName && activeTimelineIndex !== null
          ? activeTimelineIndex
          : (actor.timelineMotions[0]?.timelineIndex ?? null);

      if (!referenceName || timelineIndex === null) {
        continue;
      }

      if (!isBuiltinFreeCharacterId(actor.characterID)) {
        continue;
      }

      const sceneKeyPosePayload = serializeSceneKeyPoses(
        scene.data.objects[referenceName]?.timeline[timelineIndex]?.keyPoses,
      );
      const sceneKeyPoseSourceContent = serializeSceneKeyPoseSourceContent(
        scene.data.objects[referenceName]?.timeline[timelineIndex]?.keyPoses,
      );
      const recoveryKey = getKeyPoseRecoveryKey(
        sceneID,
        referenceName,
        timelineIndex,
      );
      const referenceState = keyPoseStore.state.referencesByName[referenceName];
      const existingLocalKeyPoseCount = referenceState?.keyPoseIds.length ?? 0;
      const hasRenderableSnapshot = Boolean(
        referenceState?.keyPoseIds.some((keyPoseId) =>
          hasKeyPoseSkeletonSnapshotTransforms(
            keyPoseStore.state.keyPosesById[keyPoseId]?.skeletonSnapshot,
          ),
        ),
      );
      const localKeyPoseSourceContent = serializeSceneKeyPoseSourceContent(
        selectKeyPoseApiPayloadForReference(keyPoseStore.state, referenceName),
      );
      const lastRecoveredPayload =
        lastRecoveredPayloadByKeyRef.current.get(recoveryKey);
      const scenePayloadChangedForReference =
        lastRecoveredPayload !== sceneKeyPosePayload;
      const needsInitialHydration =
        existingLocalKeyPoseCount === 0 && lastRecoveredPayload === undefined;
      const canHydrateSkeletonSnapshot =
        referenceName === activeReferenceName &&
        timelineIndex === activeTimelineIndex &&
        actorScene &&
        sourceBvh;
      const needsRenderableHydration =
        Boolean(canHydrateSkeletonSnapshot) &&
        existingLocalKeyPoseCount > 0 &&
        !hasRenderableSnapshot &&
        localKeyPoseSourceContent === sceneKeyPoseSourceContent;
      const shouldRecover =
        scenePayloadChangedForReference ||
        needsInitialHydration ||
        needsRenderableHydration;

      if (!shouldRecover) {
        continue;
      }

      const sceneKeyPoses = deserializeSceneKeyPoses(sceneKeyPosePayload);
      const recoveredKeyPoses = sceneKeyPoses.flatMap((keyPose, index) => {
        const sourcePose = createSourcePoseFromApiKeyPose(keyPose);
        if (!sourcePose) {
          return [];
        }

        const snapshot = canHydrateSkeletonSnapshot
          ? createKeyPoseSnapshotFromSourcePose({
              bvh: sourceBvh,
              scene: actorScene,
              sourcePose,
            })
          : null;

        return [
          {
            id: keyPose.id,
            frame: sourcePose.frame,
            skeletonSnapshot:
              snapshot?.skeletonSnapshot ??
              createEmptyKeyPoseSkeletonSnapshot(sourcePose.frame),
            sourcePose: snapshot?.sourcePose ?? sourcePose,
          } satisfies ReplaceRecoveredSceneKeyPoseInput,
        ];
      });

      keyPoseStore.replaceRecoveredSceneKeyPoses({
        referenceName,
        keyPoses: recoveredKeyPoses,
      });
      lastRecoveredPayloadByKeyRef.current.set(
        recoveryKey,
        sceneKeyPosePayload,
      );
    }
  }, [
    actorScene,
    activeReferenceName,
    activeTimelineIndex,
    keyPoseStore,
    playback.sceneActors,
    scene.data,
    sceneID,
    sourceBvh,
  ]);
}

function getKeyPoseRecoveryKey(
  sceneID: string,
  referenceName: string,
  timelineIndex: number,
) {
  return `${sceneID}:${referenceName}:${timelineIndex}`;
}
